<template>
  <b-modal
    v-model="localVisible"
    title="Assign Devices to Nurses"
    size="xl"
    hide-footer
    lazy
  >
    <!-- Filter Section with FilterSidebar -->
    <b-card class="mb-3">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Filter Devices</h5>
          <b-button
            @click="toggleSidebar"
            variant="outline-secondary"
            size="sm"
            aria-label="Toggle Filters Sidebar"
            :aria-expanded="sidebarVisible"
          >
            <i class="fas fa-filter"></i>
            Filter
            <i
              :class="
                sidebarVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
              "
              class="transition-icon"
            ></i>
          </b-button>
        </div>
      </b-card-header>
      
      <!-- Filter Sidebar -->
      <transition name="slide-fade">
        <filter-sidebar
          v-if="sidebarVisible"
          ref="filterSidebar"
          :title="'Device Filters'"
          :filters-config="deviceFiltersConfig"
          :visible="sidebarVisible"
          v-model="activeFilters"
          @filter-change="handleFilterChange"
          @filter-reset="handleFilterReset"
        >
        </filter-sidebar>
      </transition>

      <!-- Search Bar -->
      <b-card-body class="pt-3">
        <b-row>
          <b-col>
            <b-form-input
              v-model="localSearchQuery"
              placeholder="Search devices by ID, group, or site..."
              type="text"
              debounce="300"
            >
              <template #prepend>
                <i class="fas fa-search"></i>
              </template>
            </b-form-input>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Available Devices Table -->
    <b-card class="mb-3">
      <b-card-header>
        <b-row align-v="center">
          <b-col>
            <h5 class="mb-0">Available Devices ({{ filteredAvailableDevices.length }})</h5>
          </b-col>
          <b-col class="text-right">
            <b-button 
              variant="primary" 
              size="sm" 
              @click="handleOpenAssignModal"
              :disabled="localSelectedDevices.length === 0"
            >
              <i class="fas fa-plus"></i> Assign Selected ({{ localSelectedDevices.length }})
            </b-button>
          </b-col>
        </b-row>
      </b-card-header>
      
      <!-- Loading State -->
      <div v-if="loadingAssignments" class="text-center p-4">
        <b-spinner variant="primary"></b-spinner>
        <div>Loading devices...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredAvailableDevices.length === 0" class="text-center p-4">
        <i class="fas fa-search fa-2x text-muted mb-2"></i>
        <p class="text-muted">No devices found</p>
        <b-button variant="outline-primary" size="sm" @click="clearAllFilters">
          Clear All Filters
        </b-button>
      </div>

      <!-- Bootstrap Table with Checkboxes -->
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead class="thead-light">
            <tr>
              <th width="50">
                <!-- Master Checkbox -->
                <b-form-checkbox
                  v-model="allSelected"
                  @change="toggleSelectAll"
                  :indeterminate="indeterminate"
                />
              </th>
              <th>Device ID</th>
              <th>Group</th>
              <th>Site</th>
              <th>Battery</th>
              <th>State</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="device in filteredAvailableDevices" 
              :key="device.device_id"
              :class="getDeviceRowClass(device)"
            >
              <td>
                <b-form-checkbox
                  :checked="isDeviceSelected(device)"
                  @change="toggleDeviceSelection(device, $event)"
                  :disabled="device.is_assigned"
                />
              </td>
              <td><strong>{{ device.device_id }}</strong></td>
              <td>{{ device.group || 'N/A' }}</td>
              <td>{{ device.site || 'N/A' }}</td>
              <td>
                <b-badge :variant="getBatteryVariant(device.battery)">
                  {{ device.battery }}%
                </b-badge>
              </td>
              <td>
                <b-badge :variant="getStateVariant(device.state)">
                  {{ device.state }}
                </b-badge>
              </td>
              <td>
                <span v-if="device.is_assigned">
                  <b-badge variant="info">
                    Assigned to {{ device.assigned_to }}
                  </b-badge>
                </span>
                <b-badge v-else variant="success">Available</b-badge>
              </td>
              <td>
                <b-button
                  v-if="device.is_assigned"
                  size="sm"
                  variant="danger"
                  @click="$emit('unassign', device.device_id)"
                >
                  Unassign
                </b-button>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- Current Assignments Table -->
    <b-card>
      <b-card-header>
        <h5 class="mb-0">Current Assignments ({{ assignments.length }})</h5>
      </b-card-header>
      
      <!-- Loading State -->
      <div v-if="loadingAssignments" class="text-center p-4">
        <b-spinner variant="primary"></b-spinner>
        <div>Loading assignments...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="assignments.length === 0" class="text-center p-4">
        <i class="fas fa-users fa-2x text-muted mb-2"></i>
        <p class="text-muted">No active assignments</p>
      </div>

      <!-- Bootstrap Table -->
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead class="thead-light">
            <tr>
              <th>Device ID</th>
              <th>Assigned To</th>
              <th>Site</th>
              <th>Group</th>
              <th>Assigned At</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="assignment in assignments" :key="assignment.id">
              <td><strong>{{ assignment.device_id }}</strong></td>
              <td>{{ assignment.assigned_to }}</td>
              <td>{{ assignment.site || 'N/A' }}</td>
              <td>{{ assignment.group || 'N/A' }}</td>
              <td>{{ formatTime(assignment.assigned_at) }}</td>
              <td>{{ formatTime(assignment.last_activity) }}</td>
              <td>
                <b-button
                  size="sm"
                  variant="danger"
                  @click="$emit('delete-assignment', assignment.id)"
                >
                  <i class="fas fa-trash"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- Nurse Assignment Modal -->
    <!-- Nurse Assignment Modal -->
    <b-modal
      v-model="localNurseAssignmentModalVisible"
      title="Assign to Nurse"
      @ok="handleAssignDevices"
      @cancel="handleCancelAssign"
      ok-title="Assign & Provision"
      :ok-disabled="!localAssignmentForm.assigned_to || isAssigning"
      lazy
    >
      <b-form>
        <b-alert variant="info" show>
          <i class="fas fa-info-circle"></i>
          Assigning <strong>{{ localSelectedDevices.length }}</strong> device(s)
        </b-alert>

        <b-form-group label="Nurse/Staff Name *" label-for="nurse-name">
          <b-form-input
            id="nurse-name"
            v-model="localAssignmentForm.assigned_to"
            placeholder="Enter nurse name"
            required
            autofocus
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Notes (Optional)" label-for="assignment-notes">
          <b-form-textarea
            id="assignment-notes"
            v-model="localAssignmentForm.notes"
            placeholder="Optional notes..."
            rows="2"
          ></b-form-textarea>
        </b-form-group>

        <!-- Provisioning Progress - FIXED VERSION -->
        <div v-if="isAssigning" class="mt-3">
          <b-progress 
            :value="provisioningProgress" 
            :max="100" 
            show-progress 
            animated
            variant="primary"
            height="20px"
            class="mb-2"
          ></b-progress>
          <div class="mt-2 text-center">
            <small class="text-muted">{{ provisioningMessage }}</small>
          </div>
        </div>
      </b-form>
    </b-modal>
  </b-modal>
</template>

<script>
import FilterSidebar from "@/views/components/FilterSidebar.vue";

export default {
  name: 'DeviceAssignmentModal',
  components: {
    FilterSidebar
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    availableDevices: {
      type: Array,
      default: () => []
    },
    assignments: {
      type: Array,
      default: () => []
    },
    sites: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    selectedDevices: {
      type: Array,
      default: () => []
    },
    assignmentForm: {
      type: Object,
      default: () => ({
        assigned_to: '',
        notes: ''
      })
    },
    loadingAssignments: {
      type: Boolean,
      default: false
    },
    nurseAssignmentModalVisible: {
      type: Boolean,
      default: false
    },
    isAssigning: {
      type: Boolean,
      default: false
    },
    provisioningProgress: {
      type: Number,
      default: 0
    },
    provisioningMessage: {
      type: String,
      default: ''
    },
    provisioningModalVisible: {
      type: Boolean,
      default: false
    },
    isProvisioning: {
      type: Boolean,
      default: false
    },
    provisioningDevices: {
      type: Array,
      default: () => []
    },
    searchQuery: {
      type: String,
      default: ''
    },
    selectedSite: {
      type: String,
      default: null
    },
    selectedGroup: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      localSearchQuery: '',
      // Store full device objects instead of just IDs
      internalSelectedDevices: [],
      allSelected: false,
      indeterminate: false,
      
      // FILTER SIDEBAR DATA PROPERTIES
      sidebarVisible: false,
      activeFilters: {
        site: [],
        group: [],
        status: [],
        battery: [],
        assignmentStatus: [],
      },
    }
  },
  computed: {
    // Two-way computed properties for .sync props
    localVisible: {
      get() { 
        return this.visible; 
      },
      set(value) { 
        this.$emit('update:visible', value); 
      }
    },
    localNurseAssignmentModalVisible: {
      get() { return this.nurseAssignmentModalVisible; },
      set(value) { this.$emit('update:nurse-assignment-modal-visible', value); }
    },
    localAssignmentForm: {
      get() { return this.assignmentForm; },
      set(value) { this.$emit('update:assignment-form', value); }
    },

    // Handle selected devices - store full device objects
    localSelectedDevices: {
      get() {
        return this.internalSelectedDevices;
      },
      set(value) {
        this.internalSelectedDevices = value;
        this.$emit('update:selected-devices', value);
        this.updateSelectAllState();
      }
    },

    effectiveSearchQuery() {
      return this.searchQuery || this.localSearchQuery;
    },

    // DYNAMIC FILTER CONFIG FOR FILTER-SIDEBAR
    deviceFiltersConfig() {
      // Generate site options from available devices
      const siteOptions = [...new Set(this.availableDevices
        .filter(device => device.site)
        .map(device => device.site)
      )].map(site => ({ value: site, text: site }));

      // Generate group options from available devices
      const groupOptions = [...new Set(this.availableDevices
        .filter(device => device.group)
        .map(device => device.group)
      )].map(group => ({ value: group, text: group }));

      return [
        {
          id: "site",
          label: "Site",
          type: "checkbox",
          options: siteOptions,
        },
        {
          id: "group",
          label: "Group",
          type: "checkbox",
          options: groupOptions,
        },
        {
          id: "status",
          label: "Device Status",
          type: "checkbox",
          options: [
            { value: "ALLOCATED", text: "Allocated" },
            { value: "PROVISIONING", text: "Provisioning" },
            { value: "DFU", text: "DFU" },
            { value: "ERROR", text: "Error" },
            { value: "DEFECT", text: "Defect" },
            { value: "UNAVAILABLE", text: "Unavailable" },
            { value: "BOOTING", text: "Booting" },
            { value: "WAITING", text: "Waiting" },
          ],
        },
        {
          id: "battery",
          label: "Battery Level",
          type: "checkbox",
          options: [
            { value: "High", text: "High (>75%)" },
            { value: "Medium", text: "Medium (50-75%)" },
            { value: "Low", text: "Low (<50%)" },
          ],
        },
        {
          id: "assignmentStatus",
          label: "Assignment Status",
          type: "checkbox",
          options: [
            { value: "assigned", text: "Assigned" },
            { value: "available", text: "Available" },
          ],
        },
      ];
    },

    // UPDATED FILTERED DEVICES WITH FILTER SIDEBAR INTEGRATION
    filteredAvailableDevices() {
      let devices = this.availableDevices || [];
      
      // Apply Site filters
      if (this.activeFilters.site && this.activeFilters.site.length) {
        devices = devices.filter((device) =>
          this.activeFilters.site.includes(device.site)
        );
      }

      // Apply Group filters
      if (this.activeFilters.group && this.activeFilters.group.length) {
        devices = devices.filter((device) =>
          this.activeFilters.group.includes(device.group)
        );
      }

      // Apply Status filters
      if (this.activeFilters.status && this.activeFilters.status.length) {
        devices = devices.filter((device) =>
          this.activeFilters.status.includes(device.state)
        );
      }

      // Apply Battery filter
      if (this.activeFilters.battery && this.activeFilters.battery.length) {
        devices = devices.filter((device) => {
          if (this.activeFilters.battery.includes("High") && device.battery > 75) return true;
          if (this.activeFilters.battery.includes("Medium") && device.battery > 50 && device.battery <= 75) return true;
          if (this.activeFilters.battery.includes("Low") && device.battery <= 50) return true;
          return false;
        });
      }

      // Apply Assignment status filter
      if (this.activeFilters.assignmentStatus && this.activeFilters.assignmentStatus.length) {
        devices = devices.filter((device) => {
          if (this.activeFilters.assignmentStatus.includes("assigned") && device.is_assigned) return true;
          if (this.activeFilters.assignmentStatus.includes("available") && !device.is_assigned) return true;
          return false;
        });
      }

      // Apply existing search filter
      if (this.effectiveSearchQuery) {
        const query = this.effectiveSearchQuery.toLowerCase();
        devices = devices.filter(device => {
          const matches = 
            (device.device_id && device.device_id.toLowerCase().includes(query)) ||
            (device.group && device.group.toLowerCase().includes(query)) ||
            (device.site && device.site.toLowerCase().includes(query));
          return matches;
        });
      }
      
      const sorted = [...devices].sort((a, b) => {
        if (a.is_assigned && !b.is_assigned) return -1;
        if (!a.is_assigned && b.is_assigned) return 1;
        return (a.device_id || '').localeCompare(b.device_id || '');
      });

      return sorted;
    },

    // Get available devices that can be selected (not assigned)
    selectableDevices() {
      return this.filteredAvailableDevices.filter(device => !device.is_assigned);
    }
  },
  watch: {
    // Initialize local selected devices when prop changes
    selectedDevices: {
      handler(newVal) {
        // Only update if different to prevent loops
        if (JSON.stringify(newVal) !== JSON.stringify(this.internalSelectedDevices)) {
          this.internalSelectedDevices = [...newVal];
          this.updateSelectAllState();
        }
      },
      immediate: true
    },
    // Emit search query changes to parent
    localSearchQuery(newVal) {
      this.$emit('update:search-query', newVal);
    },
    // Watch for modal opening to reset selection and filters
    visible: {
      handler(newVal) {
        if (newVal) {
          // Reset selection when modal opens
          setTimeout(() => {
            this.internalSelectedDevices = [];
            this.allSelected = false;
            this.indeterminate = false;
            // Reset filter sidebar filters
            this.activeFilters = {
              site: [],
              group: [],
              status: [],
              battery: [],
              assignmentStatus: [],
            };
          }, 100);
        }
      }
    }
  },
  methods: {
    // FILTER SIDEBAR METHODS
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    
    handleFilterChange({ filterId, value }) {
      this.activeFilters[filterId] = value;
    },
    
    handleFilterReset() {
      this.activeFilters = {
        site: [],
        group: [],
        status: [],
        battery: [],
        assignmentStatus: [],
      };
    },

    clearAllFilters() {
      this.localSearchQuery = '';
      this.handleFilterReset();
    },

    // EXISTING METHODS (keeping for compatibility with parent component)
    handleSiteChange() {
      // This is now handled by the filter sidebar
      this.$emit('filter-site', this.activeFilters.site[0] || null);
    },
    
    handleGroupChange() {
      // This is now handled by the filter sidebar
      this.$emit('filter-group', this.activeFilters.group[0] || null);
    },
    
    handleOpenAssignModal() {
      this.$emit('open-assign-modal');
    },
    
    handleAssignDevices() {
      // Emit the selected devices (full objects) and assignment form
      this.$emit('assign', {
        selectedDevices: this.localSelectedDevices,
        assignmentForm: this.localAssignmentForm
      });
    },
    
    handleCancelAssign() {
      this.$emit('cancel-assign');
    },
    
    // Checkbox selection methods
    isDeviceSelected(device) {
      return this.internalSelectedDevices.some(selected => selected.device_id === device.device_id);
    },
    
    toggleDeviceSelection(device, isSelected) {
      if (isSelected) {
        // Add device object to selection
        if (!this.isDeviceSelected(device)) {
          this.localSelectedDevices = [...this.localSelectedDevices, device];
        }
      } else {
        // Remove device object from selection
        this.localSelectedDevices = this.localSelectedDevices.filter(
          selected => selected.device_id !== device.device_id
        );
      }
    },
    
    toggleSelectAll(checked) {
      if (checked) {
        // Select all selectable devices (full objects)
        this.localSelectedDevices = [...this.selectableDevices];
      } else {
        // Clear selection
        this.localSelectedDevices = [];
      }
    },
    
    updateSelectAllState() {
      const selectableCount = this.selectableDevices.length;
      const selectedCount = this.localSelectedDevices.length;
      
      if (selectedCount === 0) {
        this.allSelected = false;
        this.indeterminate = false;
      } else if (selectedCount === selectableCount) {
        this.allSelected = true;
        this.indeterminate = false;
      } else {
        this.allSelected = false;
        this.indeterminate = true;
      }
    },

    getDeviceRowClass(device) {
      return device.is_assigned ? 'assigned-device-row' : '';
    },
    getBatteryVariant(battery) {
      if (battery > 70) return 'success';
      if (battery > 30) return 'warning';
      return 'danger';
    },
    getStateVariant(state) {
      switch (state) {
        case 'ALLOCATED':
          return 'success';
        case 'PROVISIONING':
        case 'DFU':
          return 'primary';
        case 'ERROR':
        case 'DEFECT':
        case 'UNAVAILABLE':
          return 'danger';
        case 'BOOTING':
        case 'WAITING':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Berlin'
      });
    }
  },
  mounted() {
    this.updateSelectAllState();
  }
}
</script>

<style scoped>
.assigned-device-row {
  background-color: #e3f2fd !important;
  border-left: 4px solid #2196F3;
}

.assigned-device-row:hover {
  background-color: #bbdefb !important;
}

.table-sm td, .table-sm th {
  padding: 0.5rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

/* Style for disabled checkboxes */
::v-deep .custom-control-input:disabled~.custom-control-label {
  color: #6c757d;
  cursor: not-allowed;
}

::v-deep .custom-control-input:disabled~.custom-control-label::before {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

/* FILTER SIDEBAR STYLES */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.3s ease-in-out,
    opacity 0.2s ease-in-out;
  overflow: hidden;
}

.slide-fade-enter-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.4s ease-in-out 0.1s,
    opacity 0.3s ease-in-out 0.1s;
  max-height: 500px;
  overflow: hidden;
}

.slide-fade-leave-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.4s ease-in-out 0.1s,
    opacity 0.2s ease-in-out;
  max-height: 500px;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
  max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave {
  transform: translateY(0);
  opacity: 1;
  max-height: 500px;
}

.transition-icon {
  transition: transform 0.3s ease;
}
</style>